import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

interface CopyFieldProps {
  label: string;
  value: string;
  className?: string;
}

interface CopyAllButtonProps {
  fields: { label: string; value: string }[];
  className?: string;
}

const CopyField: React.FC<CopyFieldProps> = ({ label, value, className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="min-w-0 flex-1">
        <span className="font-semibold text-gray-700">{label}: </span>
        <span className="break-all text-gray-800">{value}</span>
      </div>
      <button
        onClick={handleCopy}
        title={copied ? 'Copied!' : `Copy ${label}`}
        className={`flex-shrink-0 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
          copied
            ? 'bg-emerald-100 text-emerald-600'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="mr-1" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

export const CopyAllButton: React.FC<CopyAllButtonProps> = ({ fields, className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyAll = async () => {
    const text = fields.map(f => `${f.label}: ${f.value}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopyAll}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        copied
          ? 'bg-emerald-100 text-emerald-600'
          : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
      } ${className}`}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
      {copied ? 'Copied all!' : 'Copy all'}
    </button>
  );
};

export default CopyField;
